import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import VideoPlayer from '../../../components/common/VideoPlayer/VideoPlayer.js';
import RestaurantLogo from './components/RestaurantLogo.js';
import Footer from '../../../components/layout/Footer/Footer.js';

// Sample logos, replace with actual logo paths
import BasilThaiLogo from '../../../assets/basil-thai-logo-clear V02.png';
import RubyThaiLogo from '../../../assets/ruby-thai-logo.png'
import NoriSushiLogo from '../../../assets/nori-sushi-logo V02.png'
import NoriJapanLogo from '../../../assets/nori-japan-logo-clear V02.png'
import SakeJapanLogo from '../../../assets/sake-japan-logo-clear.png';
import TheWok360Logo from '../../../assets/the-wok-360-logo.png';
import MapThai from '../../../assets/mapthailandv3.png';
import MapJapan from '../../../assets/mapjapanv3.png';
import Movie from '../../../assets/teriyaki.mp4';

const HomePage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const restaurants = [
    { name: "Basil Thai", logo: BasilThaiLogo, map: MapThai, country: "Thai", link: "/restaurants/basilthai", description: "Thai Basil inspired from the flavorful rich region of Thailand, our dishes capture the perfect balance of savory, sweet, and spice, with classics like Pad Thai and other favorites that embody the heart of Thai cuisine" },
    { name: "Sake Japan", logo: SakeJapanLogo, map: MapJapan, country: "Japan", link: "/restaurants/sakejapan", description: "Sake Japan brings the best of Japanese cuisine right to your table. From sizzling hibachi to tender teriyaki dishes, everything's made with simple, fresh ingredients. It's the perfect spot for a quick, tasty bite or a laid-back meal." },
  ];
  const otherRestaurants = [
    { name: "Ruby Thai", logo: RubyThaiLogo, map: MapThai, country: "Thai", link: "/restaurants/rubythai", description: "Ruby Basil inspired from the flavorful rich region of Thailand, our dishes capture the perfect balance of savory, sweet, and spice, with classics like Pad Thai and other favorites that embody the heart of Thai cuisine" },
    { name: "Nori Japan", logo: NoriJapanLogo, map: MapJapan, country: "Japan", link: "/restaurants/norijapan", description: "Nori Japan is all about classic Japanese flavors with a modern twist. Whether you're craving a warm bowl of teriyaki or fresh sushi, our menu has plenty of options to satisfy. Come by for a casual, flavorful meal that's always made with care." },
    { name: "Nori Sushi", logo: NoriSushiLogo, map: MapJapan, country: "Japan", link: "/restaurants/norisushi", description: "Nori Sushi serves up fresh, flavorful sushi that hits the spot. From hand-rolled sushi to delicate sashimi, we keep it simple with fresh ingredients and traditional techniques." },
    { name: "The Wok 360", logo: TheWok360Logo, map: MapJapan, country: "Thai", link: "/restaurants/thewok360", description: "The Wok 360 inspired from the flavorful rich region of Thailand, our dishes capture the perfect balance of savory, sweet, and spice, with classics like Pad Thai and other favorites that embody the heart of Thai cuisine" },
  ];

  // Automatic carousel effect with reset functionality
  useEffect(() => {
    // Create an interval that changes the active index every 5 seconds
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % restaurants.length);
    }, 5000); // Extended time to 5 seconds for better user experience

    // Clear the interval when the component unmounts or when activeIndex changes
    return () => clearInterval(interval);
  }, [restaurants.length, activeIndex]); // Added activeIndex as a dependency to reset timer when it changes

  // Handle logo click
  const handleLogoClick = (index) => {
    // Only update if clicking on a different logo
    if (index !== activeIndex) {
      setActiveIndex(index); // Manually set the active logo when clicked
      // The timer will reset because activeIndex is a dependency in the useEffect
    }
  };

  // Handle "up" and "down" arrow clicks
  const handleArrowClick = (direction) => {
    if (direction === 'up') {
      setActiveIndex((prevIndex) => (prevIndex - 1 + restaurants.length) % restaurants.length);
      // Timer will reset because activeIndex is a dependency in the useEffect
    } else if (direction === 'down') {
      setActiveIndex((prevIndex) => (prevIndex + 1) % restaurants.length);
      // Timer will reset because activeIndex is a dependency in the useEffect
    }
  };

  return (
    <div className="nav-bar">
      <div className="home-page">
        <div className="video-container">
          <VideoPlayer src={Movie} />
        </div>

        <div>
          <div id="restaurants-section" className="homepage-title">
            <div className="scroll-indicator">
              <div className="fa-solid fa-chevron-down" onClick={() => document.getElementById('restaurants-section').scrollIntoView({ behavior: 'smooth' })}></div>
              <div className="pulse-circle"></div>
            </div>
            <h3>Explore ZW Dining Group</h3>
            <p className="section-subtitle">Discover our diverse collection of culinary experiences</p>
          </div>
          
          <div id="restaurants-container" className="restaurants-container">
            <div className="restaurants-carousel-container">
              <div className="carousel-controls">
                <button className="arrow up" onClick={() => handleArrowClick('up')}>
                  <i className="fa-solid fa-chevron-up"></i>
                </button>
              </div>
              
              <section className="restaurants-logo">
                <div className="carousel-progress">
                  {restaurants.map((_, index) => (
                    <div 
                      key={`progress-${index}`} 
                      className={`progress-dot ${activeIndex === index ? 'active' : ''}`}
                      onClick={() => handleLogoClick(index)}
                    ></div>
                  ))}
                </div>
                
                {restaurants.map((restaurant, index) => (
                  <RestaurantLogo
                    key={restaurant.name}
                    isActive={activeIndex === index}
                    restaurants={restaurant.name}
                    image={<img src={restaurant.logo} alt={`${restaurant.name} Logo`} />}
                    onClick={() => handleLogoClick(index)}
                  />
                ))}
              </section>
              
              <div className="carousel-controls">
                <button className="arrow down" onClick={() => handleArrowClick('down')}>
                  <i className="fa-solid fa-chevron-down"></i>
                </button>
              </div>
            </div>

            <div className="restaurants-info-container">
              <div className="info-card">
                <div className="restaurants-about-container">
                  <div className="restaurant-title-area">
                    <h3>About {restaurants[activeIndex].name}<span className="cuisine-tag">{restaurants[activeIndex].country} Cuisine</span></h3>
                  </div>
                  <p>{restaurants[activeIndex].description}</p>
                  
                  <div className="features-list">
                    <div className="feature">
                      <i className="fa-solid fa-utensils"></i>
                      <span>Authentic Dishes</span>
                    </div>
                    <div className="feature">
                      <i className="fa-solid fa-star"></i>
                      <span>Chef's Specials</span>
                    </div>
                    <div className="feature">
                      <i className="fa-solid fa-leaf"></i>
                      <span>Fresh Ingredients</span>
                    </div>
                  </div>
                </div>
                
                <div className="restaurants-map-container">
                  <div className="restaurants-map-image">
                    <img src={restaurants[activeIndex].map} alt={`Map of ${restaurants[activeIndex].country}`} />
                    <div className="restaurants-map-country">{restaurants[activeIndex].country}</div>
                  </div>
                  <div className="restaurants-map-link">
                    <Link to={restaurants[activeIndex].link} className="explore-button">
                      <span>Explore {restaurants[activeIndex].name}</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div id="other-Restaurants-section" className="other-homepage-title">
            <h3>ZW Managed Restaurants</h3>
          </div>
          <div className="other-restaurants-container">
            <div className="other-restaurants-carousel-container">
              <div className="carousel-controls">
                <button className="arrow up fa-solid fa-chevron-up" onClick={() => handleArrowClick('up')}></button>
              </div>
              <section className="other-restaurants-logo">
                {/* <div> */}
                {otherRestaurants.map((restaurant, index) => (
                  <RestaurantLogo
                    key={restaurant.name}
                    isActive={activeIndex === index}
                    otherRestaurants={restaurant.name}
                    image={<img src={restaurant.logo} alt={`${restaurant.name} Logo`} />}
                    onClick={() => handleLogoClick(index)} // Set active logo on click
                  />
                ))}
                {/* </div> */}
              </section>
              <div className="carousel-controls">
                <button className="arrow down fa-solid fa-chevron-down" onClick={() => handleArrowClick('down')}></button>
              </div>
            </div>

            <div className="other-restaurants-info-container">
              <div className="other-restaurants-about-container">
                <h3>About {otherRestaurants[activeIndex].name}</h3>
                <p>{otherRestaurants[activeIndex].description}</p>
              </div>
              <div className="other-restaurants-map-container">
                <div className="other-restaurants-map-image">
                  <img src={otherRestaurants[activeIndex].map}></img>
                  <div className="other-restaurants-map-country">{otherRestaurants[activeIndex].country}</div>
                </div>
                <div className="other-restaurants-map-link">
                  <Link to={otherRestaurants[activeIndex].link}>Explore <br/> {otherRestaurants[activeIndex].name}</Link>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="giftcards-container">
            
          </div>

      </div>
    </div>
  );
};

export default HomePage;
